/**
 * longitudinalDiscernmentEngine.js
 *
 * Compares the CURRENT pastoral state with the HISTORICAL spiritual arc.
 * Provides temporal depth: "today appears stable, but she's been in crisis
 * for three months." Prevents Kevia from responding as if each session is isolated.
 *
 * DISTINCT from pastoralTrajectoryAnalyzer.js (which gives a single-moment snapshot).
 * This engine reasons about TIME — the gap between where someone is NOW and where
 * they have BEEN.
 *
 * Arcs:
 *   estabilidad_despues_de_crisis    — stable today, but coming from difficulty
 *   recaida_despues_de_restauracion  — regression after having recovered
 *   persistencia_en_dificultad       — difficult for a long time without change
 *   crecimiento_desde_cero           — growing from a very low starting point
 *   apertura_nueva                   — new openness with no prior crisis
 *   estabilidad_consistente          — stable throughout (unremarkable — omit)
 *   sin_historia                     — insufficient history
 */

import { getJourneySpanDays, getRecentEntries } from "./spiritualJourneyMemory";

const CRISIS_STATES   = new Set(["quebranto", "resistencia", "confusión", "agotamiento_espiritual"]);
const RECOVERY_STATES = new Set(["restauración", "apertura", "crecimiento"]);

/**
 * @param {{
 *   currentState:  string,             — from FASE 8 spiritualProcessDiscernment
 *   journeyRecord: import("./spiritualJourneyMemory").JourneyRecord|null,
 *   trajectoryStage: string,           — from kevia-relationships pastoralTrajectoryAnalyzer
 * }} params
 * @returns {{
 *   arc:             string,
 *   hasDepth:        boolean,
 *   temporalContext: string|null,      — compact instruction for system prompt
 *   spanDays:        number,
 * }}
 */
export function resolveTemporalArc({ currentState, journeyRecord, trajectoryStage }) {
  const noHistory = { arc: "sin_historia", hasDepth: false, temporalContext: null, spanDays: 0 };

  if (!journeyRecord?.entries?.length || journeyRecord.entries.length < 2) return noHistory;

  const entries  = journeyRecord.entries;
  const spanDays = getJourneySpanDays(journeyRecord);
  const pastStates = entries.slice(1).map((e) => e.processState);   // everything before now
  const isCurrentCrisis   = CRISIS_STATES.has(currentState);
  const isCurrentRecovery = RECOVERY_STATES.has(currentState);
  const hadCrisisInPast   = pastStates.some((s) => CRISIS_STATES.has(s));
  const hadRecoveryInPast = pastStates.some((s) => RECOVERY_STATES.has(s));

  // Recaída: currently in crisis, but was previously recovering
  if (isCurrentCrisis && hadRecoveryInPast && spanDays >= 7) {
    const monthsAgo = spanDays >= 30 ? `${Math.round(spanDays / 30)} mes(es)` : `${spanDays} días`;
    return {
      arc: "recaida_despues_de_restauracion",
      hasDepth: true,
      temporalContext:
        `Esta persona venía avanzando en su proceso, pero hay señales de retroceso ahora. ` +
        `Responde con paciencia pastoral renovada — no desde cero, sino reconociendo el camino recorrido.`,
      spanDays,
    };
  }

  // Estabilidad después de crisis: currently recovering/open, but came from difficulty
  if (isCurrentRecovery && hadCrisisInPast && spanDays >= 14) {
    return {
      arc: "estabilidad_despues_de_crisis",
      hasDepth: true,
      temporalContext:
        `Hay apertura hoy, pero esta persona viene de un proceso difícil. ` +
        `No respondas como si fuera una situación nueva — hay un camino real detrás.`,
      spanDays,
    };
  }

  // Persistencia en dificultad: in crisis AND has been for a while
  if (isCurrentCrisis && hadCrisisInPast && spanDays >= 21) {
    const weeks = Math.round(spanDays / 7);
    return {
      arc: "persistencia_en_dificultad",
      hasDepth: true,
      temporalContext:
        `Esta situación lleva semanas. No es una crisis momentánea. ` +
        `Acompaña con paciencia profunda — el proceso es largo.`,
      spanDays,
    };
  }

  // Crecimiento desde cero: currently growing, started from a difficult place
  if (
    (currentState === "crecimiento" || currentState === "apertura") &&
    pastStates.length >= 3 &&
    pastStates.filter((s) => CRISIS_STATES.has(s)).length >= 2
  ) {
    return {
      arc: "crecimiento_desde_cero",
      hasDepth: true,
      temporalContext:
        `Hay crecimiento real, y viene de un camino difícil. ` +
        `Este progreso merece ser reconocido — con discreción pastoral.`,
      spanDays,
    };
  }

  // Apertura nueva sin historia previa de crisis
  if (isCurrentRecovery && !hadCrisisInPast) {
    return {
      arc: "apertura_nueva",
      hasDepth: false,
      temporalContext: null,
      spanDays,
    };
  }

  // Estabilidad consistente — no need to inject anything
  return { arc: "estabilidad_consistente", hasDepth: false, temporalContext: null, spanDays };
}
